import { ApolloClient, InMemoryCache, type NormalizedCacheObject, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createHttpLink } from '@apollo/client/link/http';
import { setContext } from '@apollo/client/link/context';
import { toast } from '@/hooks/use-toast';

const dynamicMediaUrl = import.meta.env.VITE_APP_DOMAIN_NAME;

// export const GRAPHQL_URI = 'https://prd-oss-api-v2.movingwalls.com/graphql/';
// export const GRAPHQL_URI = 'https://stg-oss-api-v2.movingwalls.com/graphql/';
// export const GRAPHQL_URI = 'http://127.0.0.1:8000/graphql/';
export const GRAPHQL_URI = import.meta.env.VITE_APP_GRAPHQL_URI;
export const MAPBOX_TOKEN = 'pk.eyJ1IjoidGVqYS1zaHJlZSIsImEiOiJjbHo4OWF6ZHgwMGwxMmxzYWxzaTk1YnNiIn0.hpbbzYRq7WOaOPwInekf9w';
export const REACT_APP_PLATFORM_BASE_URL = dynamicMediaUrl ? `https://${dynamicMediaUrl}` : '';
export const MEDIA_URL = dynamicMediaUrl ?? '';

// Create auth link to dynamically get token
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    }
  };
});

// Create HTTP link
const httpLink = createHttpLink({
  uri: GRAPHQL_URI,
});

// Centralized auto-logout function
function handleAutoLogout(reason: string) {
  console.warn('Auto-logout triggered:', reason);
  
  toast({
    title: "Session Expired",
    description: "Your session has expired. Please login again.",
    variant: "destructive",
  });
  
  // Clear all authentication data
  localStorage.removeItem('authToken');
  localStorage.removeItem('auth-user');
  localStorage.removeItem('refreshToken');
  
  // Dispatch custom event for app-wide handling
  if (typeof window !== 'undefined') {
    const logoutEvent = new CustomEvent('auto-logout', {
      detail: { reason }
    });
    window.dispatchEvent(logoutEvent);
  }
  
  // Redirect after toast
  setTimeout(() => {
    window.location.href = '/';
  }, 1000);
}

// Create error link for handling authentication errors
const errorLink = onError(({ graphQLErrors, networkError, operation, response }) => {
  console.log('========== ERROR LINK TRIGGERED ==========');
  console.log('Operation:', operation.operationName);
  console.log('GraphQL Errors:', graphQLErrors);
  console.log('Network Error:', networkError);
  console.log('Network Error Full Object:', JSON.stringify(networkError, null, 2));
  
  const isMockMode = import.meta.env.VITE_APP_MOCK_DATA === "true";
  const isTestMode = import.meta.env.VITE_TEST_IP_STATUS === 'true';
  
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      console.log('GraphQL Error Details:', {
        message: err.message,
        extensions: err.extensions,
        path: err.path
      });
      
      // Check for 401 unauthorized error
      if (err.extensions?.status_code === 401 || 
          err.extensions?.code === 'UNAUTHENTICATED' ||
          err.message.toLowerCase().includes('unauthorized') || 
          err.message.toLowerCase().includes('invalid or expired') ||
          err.message.toLowerCase().includes('http 401')) {
        
        console.warn('========== GraphQL Authentication error detected:', err.message);
        
        // Skip logout in mock/test mode
        if (isMockMode || isTestMode) {
          console.log('---------->Auto-logout skipped: Mock/Test mode enabled');
          break;
        }
        
        handleAutoLogout('GraphQL auth error');
        break;
      }
    }
  }

  if (networkError) {
    console.log('---------->[Network error]:', networkError);
    
    // Check all possible status code locations
    const statusCode = (networkError as any)?.statusCode || 
                       (networkError as any)?.response?.status ||
                       (networkError as any)?.status;
    
    console.log('---------->Network error status code:', statusCode);
    console.log('---------->Network error type:', typeof networkError);
    console.log('---------->Network error keys:', Object.keys(networkError));
    
    // Also check for 401 in the error message
    const is401Error = statusCode === 401 || 
                       networkError.message?.includes('401') ||
                       networkError.message?.toLowerCase().includes('http 401') ||
                       networkError.message?.toLowerCase().includes('unauthorized');
    
    console.log('---------->Is 401 Error?', is401Error);
    
    if (is401Error) {
      console.warn('========== Network 401 error detected, logging out user ==========');
      
      // Skip logout in mock/test mode
      if (isMockMode || isTestMode) {
        console.log('Auto-logout skipped: Mock/Test mode enabled (network error)');
        return;
      }
      
      handleAutoLogout('Network 401 error');
    } else {
      console.log('---------->Network error (non-401):', {
        statusCode,
        message: networkError.message,
        name: networkError.name
      });
    }
  }
  
  console.log('========== ERROR LINK END ==========');
});

const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});

// const client = new ApolloClient({
//   link: new HttpLink({
//     uri: GRAPHQL_URI,
//     headers: {
//       authorization: localStorage.getItem('authToken') ? `Bearer ${localStorage.getItem('authToken')}` : '',
//     },
//     fetch: async (uri, options) => {
//       const response = await fetch(uri, options);
//       const text = await response.text()
//       try {
//         return new Response(text, {
//           status: response.status,
//           statusText: response.statusText,
//           headers: response.headers,
//         });
//       } catch (error) {
//         console.error('Error parsing response:', error);
//         throw error;
//       }
//     },
//   }),
//   cache: new InMemoryCache(),
// });

export { dynamicMediaUrl };
export default client;
